import {useEffect} from 'react';

/** Same rule as `heading_anchor` in helpers/ai_query/guides/models.py. */
export const headingAnchor = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^0-9a-z]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Give rendered Markdown headings ids, then scroll to the one named by `hash`.
 *
 * MarkdownView renders after mount, so this watches the container until the
 * headings appear. Ask links a guide match as `/content/guides/<id>#<anchor>`.
 */
export const useHeadingAnchors = (container: HTMLElement | null, hash: string) => {
  useEffect(() => {
    if (!container) {
      return;
    }
    const target = decodeURIComponent(hash.replace(/^#/, ''));
    let scrolled = false;
    const apply = () => {
      for (const heading of container.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6')) {
        if (!heading.id) {
          heading.setAttribute('id', headingAnchor(heading.textContent ?? ''));
        }
        if (target && !scrolled && heading.id === target) {
          heading.scrollIntoView({block: 'start'});
          scrolled = true;
        }
      }
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(container, {childList: true, subtree: true});
    return () => observer.disconnect();
  }, [container, hash]);
};
